import {
  Component,
  OnInit,
  Input,
  ElementRef,
  Renderer2,
  AfterViewInit,
  HostListener,
  OnChanges,
  SimpleChanges,
  AfterViewChecked,
  ChangeDetectionStrategy
} from '@angular/core';

@Component({
    selector: 'app-tableau-embedded-viz',
    templateUrl: './tableau-embedded-viz.component.html',
    styleUrls: ['./tableau-embedded-viz.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableauEmbeddedVizComponent implements OnInit, AfterViewInit, OnChanges, AfterViewChecked {
  @Input() dashboardIndex = 0;
  @Input() toolbar = 'hidden';
  @Input() vizUrl = '';
  @Input() token = '';
  @Input() parameters: { name: string; value: string }[] = [];

  public VizIndex = 'Tableau-Viz-' + this.dashboardIndex;
  private parametersApplied = false;

  // Original Tableau dashboard design resolution
  private readonly DESIGN_WIDTH = 1366;
  private readonly DESIGN_HEIGHT = 768;
  private readonly ASPECT_RATIO = this.DESIGN_WIDTH / this.DESIGN_HEIGHT;

  /** The width and whether the viz existed when last sized, so a check only resizes on a change. */
  private sizedFor = '';

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  /**
   * The dashboards are laid out at a fixed 1366×768, which Tableau does not reflow to a narrower
   * width: it cuts the right-hand side off. Narrower than that (a tablet, a small laptop), the
   * dashboard is rendered at its design size and scaled down to the width, so all of it shows and
   * nothing scrolls sideways. At 1366 px and wider it is rendered at the width, as before.
   */
  private calculateDashboardSize = () => {
    const parentElement = this.el.nativeElement.parentElement;
    const wrapper = this.el.nativeElement.querySelector('.tableau-wrapper');
    const vizElement = this.el.nativeElement.querySelector('tableau-viz');

    if (parentElement && wrapper) {
      const w = parentElement.offsetWidth;
      const key = `${w}:${!!vizElement}`;
      if (key === this.sizedFor) {
        return;
      }
      this.sizedFor = key;

      const scale = Math.min(1, w / this.DESIGN_WIDTH);
      const renderWidth = scale < 1 ? this.DESIGN_WIDTH : w;
      const renderHeight = scale < 1 ? this.DESIGN_HEIGHT : Math.floor(w / this.ASPECT_RATIO);

      this.renderer.setStyle(wrapper, 'width', `${w}px`);
      this.renderer.setStyle(wrapper, 'height', `${Math.floor(renderHeight * scale)}px`);

      // Tell Tableau to render at this exact size
      if (vizElement) {
        this.renderer.setAttribute(vizElement, 'width', `${renderWidth}`);
        this.renderer.setAttribute(vizElement, 'height', `${renderHeight}`);
        this.renderer.setStyle(vizElement, 'width', `${renderWidth}px`);
        this.renderer.setStyle(vizElement, 'height', `${renderHeight}px`);
        this.renderer.setStyle(vizElement, 'transform', scale < 1 ? `scale(${scale})` : 'none');
      }
    }
  };

  @HostListener('window:resize')
  onWindowResize() {
    this.calculateDashboardSize();
  }

  ngOnInit(): void {
    this.calculateDashboardSize();
  }

  ngAfterViewInit(): void {
    this.updateVizParameters();
  }

  ngAfterViewChecked(): void {
    // The viz is rendered once the URL and token arrive, after ngOnInit sized an empty wrapper.
    this.calculateDashboardSize();

    // Apply parameters after the element is conditionally rendered
    if (this.vizUrl && this.token && !this.parametersApplied) {
      this.updateVizParameters();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parameters'] && !changes['parameters'].firstChange) {
      this.parametersApplied = false;
      this.updateVizParameters();
    }
    // Reset parameters flag when vizUrl or token changes
    if (changes['vizUrl'] || changes['token']) {
      this.parametersApplied = false;
    }
  }

  private updateVizParameters(): void {
    const container = this.el.nativeElement.querySelector('tableau-viz');

    if (container) {
      // Remove existing parameters before adding new ones
      const existingParams = container.querySelectorAll('viz-parameter');
      existingParams.forEach((param: { remove: () => any; }) => param.remove());

      if (this.parameters && this.parameters.length > 0) {
        this.parameters.forEach((param) => {
          const vizParam = this.renderer.createElement('viz-parameter');
          this.renderer.setAttribute(vizParam, 'name', param.name);
          this.renderer.setAttribute(vizParam, 'value', param.value);
          this.renderer.appendChild(container, vizParam);
        });
      }
      this.parametersApplied = true;
    }
  }
}
